import Box from '@mui/material/Box'
import { palettePrimaryDark, palettePrimaryMain as PRIMARY } from '@/theme/designTokens'

import Button from '@mui/material/Button'
import IconButton from '@mui/material/IconButton'
import Stack from '@mui/material/Stack'
import Tooltip from '@mui/material/Tooltip'
import Typography from '@mui/material/Typography'

import launchDashboardGridIcon from '@/assets/campaigns/launch/icon-dashboard-grid.svg'

import type { CampaignInstrument, LineOfInquiry } from '../types'
import { LAUNCH_INSTRUMENT_GRID_COLUMNS } from '../lib/launchSurveyMetadata'
import { LaunchInstrumentRow } from './LaunchInstrumentRow'
import { UnlockLoiModal } from './UnlockLoiModal'

/** Figma 4383:4121 — only the first LOI uses this fill when Active */
const FIRST_ACTIVE_LOI_BG = '#F1FAFF'
/** Active LOI 2+ — white surface */
const OTHER_ACTIVE_LOI_BG = '#FFFFFF'
const ACTIVE_BORDER = '#E5E7EB'
/** Figma 4383:3689 — Unlock LOI outline */
const UNLOCK_OUTLINE = '#4F799C'

/** Figma 4383:3921 — up next LOI card */
const UPCOMING_LOI_BG = '#F6F9FB'
const UPCOMING_LOI_BORDER = '#E1E7EF'

const EMPTY_LOI_INSTRUMENTS_MESSAGE = 'No instruments deployed yet'
const CREATE_INSTRUMENT_LABEL = '+ Create Instrument'

/**
 * US-SM-3-1-5 / US-SM-3-1-6 — LOI 1 is active on launch; tolerate legacy rows where
 * lifecycle was never persisted as `active` while later LOIs were unlocked manually.
 */
function isLoiActiveOnLaunchScreen(loi: LineOfInquiry, ordinal: number): boolean {
  if (loi.lifecycleStatus === 'active') return true
  if (ordinal === 1 && loi.lifecycleStatus !== 'up_next') return true
  return false
}

interface Props {
  loi: LineOfInquiry
  ordinal: number
  instrumentsForLoi: CampaignInstrument[]
  canWrite: boolean
  /** When true, LOI actions are disabled and Active badges are hidden. */
  campaignEnded?: boolean
  unlockOpen: boolean
  onUnlockOpen: () => void
  onUnlockClose: () => void
  onConfirmUnlock: () => Promise<void>
  unlockPending: boolean
  onCreateInstrument: (loiId: string) => void
  onNavigateInstrumentDashboard: (instrumentId: string) => void
  onNavigateLoiResponseDashboard?: (loiId: string) => void
  onContinueInstrument: (instrumentId: string) => void
  onDeleteDraftClick?: (instrument: CampaignInstrument) => void
  onReactivateClick: (instrument: CampaignInstrument) => void
  onPauseClick: (instrument: CampaignInstrument) => void
  onStopClick: (instrument: CampaignInstrument) => void
  /** BFP-1142 — edit a Live / Paused survey. */
  onEditLiveClick?: (instrument: CampaignInstrument) => void
  /** BFP-1158 — send a reminder for a Live survey. */
  onRemindClick?: (instrument: CampaignInstrument) => void
}

/** Figma 4383:3746 — LOI ● Active pill */
const ACTIVE_STATUS_BG = '#1AA24C'

function resolveCardBg(isActive: boolean, ordinal: number): string {
  if (!isActive) {
    return UPCOMING_LOI_BG
  }
  if (ordinal === 1) {
    return FIRST_ACTIVE_LOI_BG
  }
  return OTHER_ACTIVE_LOI_BG
}

function resolveSubtitleBottomMargin(
  isActive: boolean,
  instrumentCount: number,
): number | string {
  if (!isActive) {
    return 1
  }
  if (instrumentCount > 0) {
    return '7px'
  }
  return '10px'
}

function ActiveStatusBadge({ testId }: Readonly<{ testId: string }>) {
  return (
    <Box
      component="span"
      data-testid={testId}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        flexShrink: 0,
        gap: '6px',
        px: '10px',
        py: '2px',
        borderRadius: '9999px',
        bgcolor: ACTIVE_STATUS_BG,
        color: '#FFFFFF',
        fontFamily: '"Inter", sans-serif',
        fontWeight: 500,
        fontSize: '12px',
        lineHeight: '16px',
        letterSpacing: 0,
      }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          width: 6,
          height: 6,
          borderRadius: '50%',
          bgcolor: '#FFFFFF',
          flexShrink: 0,
        }}
      />
      <Box component="span" sx={{ lineHeight: '16px' }}>
        Active
      </Box>
    </Box>
  )
}

function hasLiveInstruments(instruments: CampaignInstrument[]): boolean {
  return instruments.some((inst) => inst.status !== 'draft')
}

function LaunchLoiCardTitleRow(
  props: Readonly<{
    loi: LineOfInquiry
    ordinal: number
    title: string
    instrumentsForLoi: CampaignInstrument[]
    campaignEnded: boolean
    onNavigateLoiResponseDashboard?: (loiId: string) => void
  }>,
) {
  const { loi, ordinal, title, instrumentsForLoi, campaignEnded, onNavigateLoiResponseDashboard } =
    props
  const isActive = isLoiActiveOnLaunchScreen(loi, ordinal)
  const showActiveBadge = isActive && !campaignEnded
  const showResponseDashboard =
    isActive &&
    onNavigateLoiResponseDashboard != null &&
    hasLiveInstruments(instrumentsForLoi)

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ mb: 0.5 }}
    >
      <Typography
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontWeight: 600,
          fontSize: '14px',
          lineHeight: '20px',
          color: '#0F1729',
          pr: 1,
          flex: 1,
        }}
      >
        {title}
      </Typography>
      <Stack direction="row" spacing={1.25} alignItems="center">
        {showResponseDashboard ? (
          <Tooltip title="Response Dashboard">
            <IconButton
              size="small"
              aria-label="Open LOI response dashboard"
              data-testid={`launch-loi-response-dashboard-${loi.id}`}
              onClick={() => onNavigateLoiResponseDashboard(loi.id)}
              sx={{
                width: 28,
                height: 28,
                p: 0.5,
                flexShrink: 0,
                color: '#008CD0',
                '&:hover': { bgcolor: 'rgba(0, 140, 208, 0.08)' },
                '&:focus-visible': {
                  outline: '2px solid #008CD0',
                  outlineOffset: 2,
                },
              }}
            >
              <Box
                component="img"
                src={launchDashboardGridIcon}
                alt=""
                aria-hidden
                sx={{
                  width: 15,
                  height: 15,
                  display: 'block',
                  objectFit: 'contain',
                }}
              />
            </IconButton>
          </Tooltip>
        ) : null}
        {showActiveBadge ? (
          <ActiveStatusBadge testId={`launch-loi-active-badge-${loi.id}`} />
        ) : isActive ? null : (
          <Box
            component="span"
            data-testid={`launch-loi-up-next-${loi.id}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              minHeight: 20,
              px: '10px',
              py: '2px',
              borderRadius: '9999px',
              bgcolor: '#F1F5F9',
              fontFamily: '"Inter", sans-serif',
              fontSize: '12px',
              fontWeight: 500,
              lineHeight: '16px',
              color: '#65758B',
            }}
          >
            Up Next
          </Box>
        )}
      </Stack>
    </Stack>
  )
}

function sumInstrumentResponses(instruments: CampaignInstrument[]): number {
  return instruments.reduce((sum, i) => sum + (i.responseCount ?? 0), 0)
}

function LaunchLoiMetaLine(
  props: Readonly<{
    loi: LineOfInquiry
    isActive: boolean
    instrumentsForLoi: CampaignInstrument[]
    subtitleMb: number | string
  }>,
) {
  const { loi, isActive, instrumentsForLoi, subtitleMb } = props

  // Empty active LOI: hide the meta line — the centered empty-state message covers it.
  if (isActive && instrumentsForLoi.length === 0) {
    return null
  }

  const totalResponses = sumInstrumentResponses(instrumentsForLoi)

  return (
    <Typography
      sx={{
        fontFamily: '"Inter", sans-serif',
        fontWeight: 400,
        fontSize: isActive ? '12px' : '14px',
        lineHeight: isActive ? '16px' : '20px',
        color: '#65758B',
        mb: subtitleMb,
      }}
    >
      {isActive ? (
        <>
          {instrumentsForLoi.length} instruments ·{' '}
          {totalResponses.toLocaleString()} responses
        </>
      ) : (
        loi.description || '\u00A0'
      )}
    </Typography>
  )
}

function LaunchLoiCreateInstrumentButton(
  props: Readonly<{
    loiId: string
    campaignEnded: boolean
    onCreateInstrument: (loiId: string) => void
    alignSelf?: 'flex-end' | 'auto'
  }>,
) {
  const { loiId, campaignEnded, onCreateInstrument, alignSelf = 'auto' } = props

  return (
    <Button
      variant="contained"
      disabled={campaignEnded}
      data-testid={`launch-loi-create-instrument-${loiId}`}
      onClick={() => onCreateInstrument(loiId)}
      sx={{
        alignSelf: { xs: 'flex-end', sm: alignSelf },
        flexShrink: 0,
        borderRadius: '12px',
        textTransform: 'none',
        fontFamily: '"Inter", sans-serif',
        fontWeight: 500,
        fontSize: '14px',
        lineHeight: '20px',
        letterSpacing: '0.1px',
        px: '16px',
        py: '10px',
        minHeight: 40,
        bgcolor: PRIMARY,
        color: '#FFFFFF',
        boxShadow: 'none',
        '&:hover': { bgcolor: palettePrimaryDark, boxShadow: 'none' },
      }}
    >
      {CREATE_INSTRUMENT_LABEL}
    </Button>
  )
}

function LaunchLoiEmptyInstrumentsSection(
  props: Readonly<{ loiId: string }>,
) {
  const { loiId } = props

  return (
    <Box
      data-testid={`launch-loi-empty-instruments-${loiId}`}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: 72,
        py: '12px',
      }}
    >
      <Typography
        data-testid={`launch-loi-empty-message-${loiId}`}
        sx={{
          fontFamily: '"Inter", sans-serif',
          fontWeight: 400,
          fontSize: '14px',
          lineHeight: '20px',
          color: '#65758B',
          textAlign: 'center',
        }}
      >
        {EMPTY_LOI_INSTRUMENTS_MESSAGE}
      </Typography>
    </Box>
  )
}

function LaunchLoiInstrumentsSection(
  props: Readonly<{
    loiOrdinal: number
    instrumentsForLoi: CampaignInstrument[]
    canWrite: boolean
    onNavigateInstrumentDashboard: (instrumentId: string) => void
    onContinueInstrument: (instrumentId: string) => void
    onDeleteDraftClick?: (instrument: CampaignInstrument) => void
    onReactivateClick: (instrument: CampaignInstrument) => void
    onPauseClick: (instrument: CampaignInstrument) => void
    onStopClick: (instrument: CampaignInstrument) => void
    onEditLiveClick?: (instrument: CampaignInstrument) => void
    onRemindClick?: (instrument: CampaignInstrument) => void
  }>,
) {
  const {
    loiOrdinal,
    instrumentsForLoi,
    canWrite,
    onNavigateInstrumentDashboard,
    onContinueInstrument,
    onDeleteDraftClick,
    onReactivateClick,
    onPauseClick,
    onStopClick,
    onEditLiveClick,
    onRemindClick,
  } = props

  return (
    <Box
      data-testid="launch-instrument-table"
      sx={{ py: '12px' }}
    >
      <Box
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: LAUNCH_INSTRUMENT_GRID_COLUMNS,
          bgcolor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          borderRadius: '10px',
          overflow: 'hidden',
        }}
      >
        {(
          [
            'Instrument',
            'Audience selection',
            'Response /\nper Reward',
            'Published',
            'Status',
            'Action',
          ] as const
        ).map((label) => (
          <Typography
            key={label}
            component="span"
            sx={{
              minWidth: 0,
              px: '13px',
              py: '8px',
              display: 'flex',
              alignItems: 'center',
              minHeight: 49,
              bgcolor: '#e1e7f3',
              borderBottom: '1px solid #E5E7EB',
              fontFamily: '"Inter", sans-serif',
              fontWeight: 600,
              fontSize: '12px',
              lineHeight: '16px',
              letterSpacing: '0.6px',
              textTransform: 'uppercase',
              color: '#4a5565',
              whiteSpace: 'pre-line',
            }}
          >
            {label}
          </Typography>
        ))}
        {instrumentsForLoi.map((inst) => (
          <LaunchInstrumentRow
            key={inst.id}
            instrument={inst}
            loiOrdinal={loiOrdinal}
            subgrid
            canWrite={canWrite}
            onNavigateDashboard={onNavigateInstrumentDashboard}
            onContinueDraft={onContinueInstrument}
            onDeleteDraft={onDeleteDraftClick ?? (() => undefined)}
            onReactivateClick={onReactivateClick}
            onPauseClick={onPauseClick}
            onStopClick={onStopClick}
            onEditLiveClick={onEditLiveClick}
            onRemindClick={onRemindClick}
          />
        ))}
      </Box>
    </Box>
  )
}

function LaunchLoiTagsAndActions(
  props: Readonly<{
    loi: LineOfInquiry
    isActive: boolean
    isUpNext: boolean
    canWrite: boolean
    campaignEnded: boolean
    onCreateInstrument: (loiId: string) => void
    onUnlockOpen: () => void
  }>,
) {
  const {
    loi,
    isActive,
    isUpNext,
    canWrite,
    campaignEnded,
    onCreateInstrument,
    onUnlockOpen,
  } = props

  return (
    <Stack
      direction={{ xs: 'column', sm: 'row' }}
      justifyContent="flex-end"
      alignItems={{ xs: 'stretch', sm: 'flex-end' }}
      spacing={{ xs: 1.5, sm: 0 }}
      sx={{ gap: { sm: 2 } }}
    >
      {isActive && canWrite ? (
        <LaunchLoiCreateInstrumentButton
          loiId={loi.id}
          campaignEnded={campaignEnded}
          onCreateInstrument={onCreateInstrument}
        />
      ) : null}
      {isUpNext && canWrite ? (
        <Button
          variant="outlined"
          disabled={campaignEnded}
          data-testid={`launch-loi-unlock-trigger-${loi.id}`}
          onClick={onUnlockOpen}
          sx={{
            alignSelf: { xs: 'flex-end', sm: 'auto' },
            flexShrink: 0,
            borderRadius: '12px',
            textTransform: 'none',
            fontFamily: '"Inter", sans-serif',
            fontWeight: 500,
            fontSize: '14px',
            lineHeight: '20px',
            letterSpacing: '0.1px',
            px: '12px',
            py: '6px',
            minHeight: 36,
            borderWidth: 1,
            borderColor: UNLOCK_OUTLINE,
            color: UNLOCK_OUTLINE,
            '&:hover': {
              borderColor: UNLOCK_OUTLINE,
              bgcolor: 'rgba(79, 121, 156, 0.06)',
            },
          }}
        >
          Unlock LOI
        </Button>
      ) : null}
    </Stack>
  )
}

export function LaunchLoiCard(props: Readonly<Props>) {
  const {
    loi,
    ordinal,
    instrumentsForLoi,
    canWrite,
    campaignEnded = false,
    unlockOpen,
    onUnlockOpen,
    onUnlockClose,
    onConfirmUnlock,
    unlockPending,
    onCreateInstrument,
    onNavigateInstrumentDashboard,
    onNavigateLoiResponseDashboard,
    onContinueInstrument,
    onDeleteDraftClick,
    onReactivateClick,
    onPauseClick,
    onStopClick,
    onEditLiveClick,
    onRemindClick,
  } = props

  const isActive = isLoiActiveOnLaunchScreen(loi, ordinal)
  const isUpNext = loi.lifecycleStatus === 'up_next'
  const cardBg = resolveCardBg(isActive, ordinal)
  const title = `LOI ${ordinal}: ${loi.title}`
  const fullTitleLabel = title
  const hasInstruments = instrumentsForLoi.length > 0
  const showInstrumentList = isActive === true && hasInstruments
  const showEmptyInstrumentsSection = isActive && !hasInstruments
  const subtitleMb = resolveSubtitleBottomMargin(
    isActive,
    instrumentsForLoi.length,
  )

  return (
    <Box sx={{ mb: '24px' }}>
      <Box
        data-testid={`launch-loi-card-${loi.id}`}
        sx={{
          border: `1px solid ${isActive ? ACTIVE_BORDER : UPCOMING_LOI_BORDER}`,
          borderRadius: isActive ? '10px' : '8px',
          bgcolor: cardBg,
          boxShadow: isActive ? 'none' : '0px 1px 1px rgba(0,0,0,0.05)',
          pl: isActive ? '35px' : '21px',
          pr: isActive ? '32px' : '21px',
          pt: isActive ? '19px' : '21px',
          pb: isActive ? '16px' : '21px',
          position: 'relative',
        }}
      >
        <LaunchLoiCardTitleRow
          loi={loi}
          ordinal={ordinal}
          title={title}
          instrumentsForLoi={instrumentsForLoi}
          campaignEnded={campaignEnded}
          onNavigateLoiResponseDashboard={onNavigateLoiResponseDashboard}
        />
        <LaunchLoiMetaLine
          loi={loi}
          isActive={isActive}
          instrumentsForLoi={instrumentsForLoi}
          subtitleMb={subtitleMb}
        />
        {showInstrumentList ? (
          <LaunchLoiInstrumentsSection
            loiOrdinal={ordinal}
            instrumentsForLoi={instrumentsForLoi}
            canWrite={canWrite}
            onNavigateInstrumentDashboard={onNavigateInstrumentDashboard}
            onContinueInstrument={onContinueInstrument}
            onDeleteDraftClick={onDeleteDraftClick}
            onReactivateClick={onReactivateClick}
            onPauseClick={onPauseClick}
            onStopClick={onStopClick}
            onEditLiveClick={onEditLiveClick}
            onRemindClick={onRemindClick}
          />
        ) : null}
        {showEmptyInstrumentsSection ? (
          <LaunchLoiEmptyInstrumentsSection loiId={loi.id} />
        ) : null}
        <LaunchLoiTagsAndActions
          loi={loi}
          isActive={isActive}
          isUpNext={isUpNext}
          canWrite={canWrite}
          campaignEnded={campaignEnded}
          onCreateInstrument={onCreateInstrument}
          onUnlockOpen={onUnlockOpen}
        />
      </Box>

      <UnlockLoiModal
        open={unlockOpen}
        confirmationTitle={`Unlock LOI ${ordinal} ?`}
        fullTitleLabel={fullTitleLabel}
        previousLoiOrdinal={Math.max(1, ordinal - 1)}
        onClose={onUnlockClose}
        onConfirm={onConfirmUnlock}
        confirming={unlockPending}
      />
    </Box>
  )
}
